import { createContext, createElement, useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifyDiagnosisTicketCreated } from "./ticket-created-event";

import type {
  CreateTicketFromDiagnosisInput,
  ResolvedTicketDetails,
  ResolvedTicketsQuery,
  SyncResolutionStatusesInput,
  TicketResolutionService,
  UpdateResolutionMetadataInput,
} from "./contracts";
import { registerTicketResolutionTranslations } from "./i18n";

const TicketResolutionServicesContext = createContext<TicketResolutionService | null>(null);

export function TicketResolutionProvider({
  service,
  children,
}: {
  service: TicketResolutionService;
  children: React.ReactNode;
}) {
  registerTicketResolutionTranslations();

  return createElement(
    TicketResolutionServicesContext.Provider,
    { value: service },
    children,
  );
}

function useTicketResolutionService(): TicketResolutionService {
  const service = useContext(TicketResolutionServicesContext);
  if (service === null) {
    throw new Error(
      "TicketResolutionProvider is missing. Wrap the Dashboard app with the provider.",
    );
  }
  return service;
}

const queryKeys = {
  root: ["bitsentry", "dashboard-ticket-resolution"] as const,
  diagnosisTickets: (diagnosisIds?: number[]) =>
    [...queryKeys.root, "diagnosis-tickets", diagnosisIds ?? []] as const,
  resolvedTicket: (id: string) => [...queryKeys.root, "resolved-ticket", id] as const,
  resolvedTickets: (filters: ResolvedTicketsQuery = {}) =>
    [...queryKeys.root, "resolved-tickets", filters] as const,
  resolvedSummary: () => [...queryKeys.root, "resolved-summary"] as const,
};

export function useDiagnosisTickets(diagnosisIds?: number[]) {
  const service = useTicketResolutionService();
  return useQuery({
    queryKey: queryKeys.diagnosisTickets(diagnosisIds),
    queryFn: () => service.getDiagnosisTickets(diagnosisIds),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useCreateDiagnosisTicket() {
  const service = useTicketResolutionService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketFromDiagnosisInput) =>
      service.createDiagnosisTicket(input),
    onSuccess: () => {
      notifyDiagnosisTicketCreated();
      void queryClient.invalidateQueries({ queryKey: queryKeys.root });
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === "dashboard" && queryKey[2] === "incidents",
      });
    },
  });
}

export function useResolvedTickets(filters: ResolvedTicketsQuery = {}) {
  const service = useTicketResolutionService();
  return useQuery({
    queryKey: queryKeys.resolvedTickets(filters),
    queryFn: () => service.getResolvedTickets(filters),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useResolvedTicket(id?: string, enabled = true) {
  const service = useTicketResolutionService();
  return useQuery({
    queryKey: queryKeys.resolvedTicket(id ?? ""),
    queryFn: () => {
      if (id === undefined || id.length === 0) {
        return Promise.resolve<ResolvedTicketDetails | null>(null);
      }
      return service.getResolvedTicket(id);
    },
    enabled: enabled && typeof id === "string" && id.length > 0,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
  });
}

export function useResolvedSummary() {
  const service = useTicketResolutionService();
  return useQuery({
    queryKey: queryKeys.resolvedSummary(),
    queryFn: () => service.getResolvedSummary(),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useUpdateResolutionMetadata() {
  const service = useTicketResolutionService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateResolutionMetadataInput) =>
      service.updateResolutionMetadata(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.root });
    },
  });
}

export function useSendResolutionNotesToItop() {
  const service = useTicketResolutionService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => service.sendResolutionNotesToItop(id),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.root });
    },
  });
}

export function useSyncResolutionStatuses() {
  const service = useTicketResolutionService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input?: SyncResolutionStatusesInput) =>
      service.syncResolutionStatuses(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.root });
    },
  });
}

export function useSyncTicketStatus() {
  const service = useTicketResolutionService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => service.syncTicketStatus(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.root });
    },
  });
}

export type {
  CreateTicketFromDiagnosisInput,
  DiagnosisTicket,
  PaginatedResolvedTickets,
  ResolvedTicketDetails,
  ResolvedTicketsQuery,
  ResolvedTicketsSummary,
  SyncResolutionStatusesInput,
  UpdateResolutionMetadataInput,
} from "./contracts";
